// Client-side helper for the /api/auth/log beacon (#184).
//
// `@/lib/auth/log-event` is server-only (uses node:crypto + reads the
// AUTH_LOG_EMAIL_PEPPER env var). Client components cannot import it. This
// helper POSTs to /api/auth/log instead, which forwards to logAuthEvent
// server-side where the pepper hash can run.
//
// Beacon-style semantics:
// - keepalive=true so a navigation that fires immediately after the beacon
//   (window.location.assign on login success) doesn't cancel the request
// - failures are swallowed — auth events are observability, not critical
//   path. We never want a logging failure to block a login or a signout.
// - pure client-side; never import from server code (use logAuthEvent there).

import type { AuthEventName } from '@/lib/auth/log-event';

// Mirror the server-side AuthEventContext closed shape, but client-side we
// only ever pass the small subset of fields the beacon route accepts. The
// route strips anything outside this set; passing extras is a wasted byte
// on the wire.
export type ClientAuthEventContext = {
  tenant_id?: string | null;
  user_id?: string | null;
  email?: string | null;
  reason?: string | null;
};

export function postAuthEventBeacon(
  event: AuthEventName,
  context: ClientAuthEventContext = {}
): void {
  // Fire-and-forget. We never await this from a UI handler — the user-
  // visible action (sign-in redirect, password change confirmation, etc.)
  // proceeds independently.
  try {
    void fetch('/api/auth/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, context }),
      keepalive: true,
    }).catch(() => {
      // Swallow network errors. Datadog dashboards will show a gap in event
      // counts if the route is down, which is the right signal — better than
      // breaking the user-visible flow.
    });
  } catch {
    // fetch can throw synchronously in some edge environments. Same policy.
  }
}
